<script lang="ts" setup>
import type { MetaSEO } from '~/types/seo'
import type { Story } from '~/types/storyblok'
import type { ArticleDetailPageSchema } from '~/types/storyblok/article-detail-page'
import { withQuery } from 'ufo'
import { getMainStory } from '~/lib/content/getMainStory'
import { generateSeoLinks } from '~/lib/seo'
import { getStoryApiUrlParams } from '~/lib/storyblok'
import { storyblokResolveLinks } from '~/lib/storyblokResolveLinks'
import { useLanguage, useStoryLocale } from '~/composables/useLanguage'
import { useMeta } from '~/composables/useMeta'
import { useStoryblokMode } from '~/composables/useStoryblokMode'
import { useStorefrontStore } from '~/stores/storefront'

/* Composables */
const route = useRoute()
const router = useRouter()
const { language } = useLanguage()
const { contentKey, currentStorefrontCode } = storeToRefs(useStorefrontStore())
const { storyblokContentVersion } = useStoryblokMode()
const storyblokApi = useStoryblokApi()
const storefrontStore = useStorefrontStore()

/* Data fetching */
const storyPath = `/faq/${route.params.faq_section}`
const storyKey = `story-${language}-${storyPath}`
const storyUrl = withQuery(storyPath, route.query)
const storyLocale = useStoryLocale()
// Keyed on the path alone: ISR strips every query param but `page` from the
// server render, so a query-bearing key (e.g. ?scrollTo=career) would miss the
// payload on the client and refetch, blanking the page.
const { data: story, error } = await useAsyncData(storyKey, async () => {
  const storyblokUrlParams = getStoryApiUrlParams(storyUrl, {
    contentKey: contentKey.value,
    language: storyLocale(),
    version: storyblokContentVersion.value,
    contentType: 'article-detail-page',
    resolveLinks: 'url',
  })

  const { data } = await storyblokApi.get(`cdn/stories`, storyblokUrlParams)
  let story = getMainStory(data.stories, contentKey.value, currentStorefrontCode.value)
  if (!story) {
    console.warn(`Storyblok data not found for ${storyUrl} and request ${withQuery(
      'https://api.storyblok.com/v2/cdn/stories',
      JSON.parse(JSON.stringify(storyblokUrlParams)),
    )}`)
    await router.replace(`/${route.params.storefront}/faq`)
    return
  }

  story = storyblokResolveLinks(story)
  return story as Story<ArticleDetailPageSchema>
})

/** Scroll logic */

function checkScrollTo() {
  const scrollTo = route.query.scrollTo
  if (process.server || !scrollTo || !story.value)
    return

  const element = document.getElementById(scrollTo.toString())
  if (!element)
    return

  window.scrollTo({ top: element.getBoundingClientRect().top - 175, behavior: 'smooth' })
}
onMounted(() => {
  checkScrollTo()
})
watch(story, async () => {
  await nextTick()
  checkScrollTo()
})

/* Error handling */
if (error.value)
  console.error('Storyblok data not found', error.value)
if (error.value && !story.value) {
  throw createError({
    statusCode: 404,
    message: error.value?.message || 'Storyblok data not found',
  })
}

/* Storyblok bridge */
watch(
  story,
  (newStory, oldStory) => {
    if (process.server || !newStory?.id)
      return
    if (!oldStory?.id || oldStory.id !== newStory.id)
      useStoryblokBridge(newStory.id, storyUpdate => (story.value = storyUpdate as any))
  },
  { immediate: true },
)

/* META */
const fallbackMeta: MetaSEO = { title: '', description: '' }
const { getMeta } = useMeta({
  story: null,
  storefront: storefrontStore.current,
  market: storefrontStore.currentMarket,
  SEO: story.value?.content.seo || fallbackMeta,
})
const meta = [...getMeta()]
useHead({
  title: meta[0]?.content,
  meta,
  link: generateSeoLinks(route),
})
</script>

<template>
  <main
    v-if="story"
    v-editable="story.content"
  >
    <component
      :is="content.component"
      v-for="content in story.content.content"
      :id="'id' in content ? content.id : undefined"
      :key="content._uid"
      :blok="content"
      :story="content"
    />
  </main>
</template>
